import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
import PartnerLayout from './components/Partner/PartnerLayout';
import UserDetails from './components/UserDetails/UserDetails';
import Requests from './components/Requests/Requests';
import Dashboard from './components/Dashboard/Dashboard';
import WingmanIntelligence from './components/WingmanIntelligence/WingmanIntelligence';
import Subscriptions from './components/Subscriptions';
import Feedback from './components/Feedback';
import Events from './components/Events/Events';
import EventDetails from './components/Events/EventDetails';
import ManageEvent from './components/Events/ManageEvent/ManageEvent';
import Posts from './components/Posts';
import PostDetails from './components/PostDetails';
import MessageDetails from './components/MessageDetails';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import PartnerLogin from './components/Auth/PartnerLogin';
import AdminManagement from './components/AdminManagement/AdminManagement';
import PartnerManagement from './components/PartnerManagement/PartnerManagement';
import PartnerDashboard from './components/Partner/PartnerDashboard';
import PartnerEvents from './components/Partner/Events/PartnerEvents';
import PartnerCommunity from './components/Partner/Community/PartnerCommunity';
import PartnerModerationFeedback from './components/Partner/Events/PartnerModerationFeedback';
import PartnerPlaceholderPage from './components/Partner/PartnerPlaceholderPage';
import PortalNotificationsPage from './components/Notifications/PortalNotificationsPage';
import { ThemeModeProvider } from './contexts/ThemeModeProvider';
import { NotificationProvider } from './contexts/NotificationProvider';
import { SocketProvider } from './contexts/SocketProvider';
import PartnerSocketListeners from './components/PartnerSocketListeners';
import { useAuth } from './contexts/AuthContext';
import { EventsScopeProvider, EVENTS_SCOPE } from './contexts/EventsScopeContext';
import { USER_ROLES } from './constants';
import { getLoginPathFromPathname } from './services/auth';
import PartnerSettings from './components/PartnerSettings/PartnerSettings';
import PartnerManageSubscription from './components/Partner/Subscription/PartnerManageSubscription';
import SignOutOverlay from './components/SignOutOverlay';
import VettedCommunityUsers from './components/VettedCommunityUsers/VettedCommunityUsers';
import VettedCommunityUserDetails from './components/VettedCommunityUsers/VettedCommunityUserDetails';
import VettedCommunityConfig from './components/VettedCommunityUsers/VettedCommunityConfig';
import Users from './components/Users/Users';
import AriaHandsoff from './components/AriaHandsoff/AriaHandsoff';

const LoggedOutFallbackRedirect = () => {
  const { pathname } = useLocation();
  return <Navigate replace to={getLoginPathFromPathname(pathname)} />;
};

const App = () => {
  const { userRole, loading, signOut, signingOut } = useAuth();

  return (
    <ThemeModeProvider>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Router>
          {signingOut ? <SignOutOverlay /> : null}
          <NotificationProvider>
            <SocketProvider>
              <PartnerSocketListeners />
              <Toaster
                position="top-right"
                containerStyle={{ top: 72, right: 16, zIndex: 1400 }}
                toastOptions={{
                  duration: 10000,
                  style: { pointerEvents: 'auto', maxWidth: '100%' },
                }}
              />
              {!userRole ? (
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/partner-login" element={<PartnerLogin />} />
                  <Route path="*" element={<LoggedOutFallbackRedirect />} />
                </Routes>
              ) : userRole === USER_ROLES.PARTNER ? (
                <PartnerLayout onSignOut={signOut}>
                  <EventsScopeProvider scope={EVENTS_SCOPE.PARTNER}>
                    <Routes>
                      <Route path="/" element={<Navigate replace to="/partner-dashboard" />} />
                      <Route path="/partner-dashboard" element={<PartnerDashboard />} />
                      <Route path="/partner-events" element={<PartnerEvents />} />
                      <Route path="/partner-community" element={<PartnerCommunity />} />
                      <Route path="/partner-aria-handsoff" element={<AriaHandsoff />} />
                      <Route
                        path="/aria-handsoff"
                        element={<Navigate replace to="/partner-aria-handsoff" />}
                      />
                      <Route path="/partner-events/new" element={<ManageEvent />} />
                      <Route path="/partner-events/:eventId/edit" element={<ManageEvent />} />
                      <Route path="/partner-events/:eventId" element={<EventDetails />} />
                      <Route
                        path="/partner-events/:eventId/moderation-feedback"
                        element={<PartnerModerationFeedback />}
                      />
                      <Route
                        path="/partner-attendees"
                        element={<PartnerPlaceholderPage title="Attendees" />}
                      />
                      <Route
                        path="/partner-analytics"
                        element={<PartnerPlaceholderPage title="Analytics" />}
                      />
                      <Route path="/partner-subscription" element={<PartnerManageSubscription />} />
                      <Route
                        path="/partner-settings"
                        element={<PartnerSettings title="Settings" />}
                      />
                      <Route path="/partner-notifications" element={<PortalNotificationsPage />} />
                      <Route path="/login" element={<Navigate replace to="/partner-dashboard" />} />
                      <Route
                        path="/partner-login"
                        element={<Navigate replace to="/partner-dashboard" />}
                      />
                      <Route path="*" element={<Navigate replace to="/partner-dashboard" />} />
                    </Routes>
                  </EventsScopeProvider>
                </PartnerLayout>
              ) : (
                <Layout onSignOut={signOut}>
                  <EventsScopeProvider scope={EVENTS_SCOPE.ADMIN}>
                    <Routes>
                      <Route path="/" element={<Navigate replace to="/dashboard" />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/wingman-intelligence" element={<WingmanIntelligence />} />
                      <Route path="/subscriptions" element={<Subscriptions />} />
                      <Route path="/requests" element={<Requests />} />
                      <Route path="/posts" element={<Posts />} />
                      <Route path="/post-details/:postId" element={<PostDetails />} />
                      <Route path="/message-details/:messageId" element={<MessageDetails />} />
                      <Route path="/user/:userId" element={<UserDetails />} />
                      <Route path="/feedback" element={<Feedback />} />
                      <Route path="/admin-management" element={<AdminManagement />} />
                      <Route path="/partner-management" element={<PartnerManagement />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/vetted-users" element={<VettedCommunityUsers />} />
                      <Route path="/vetted-users/config" element={<VettedCommunityConfig />} />
                      <Route path="/vetted-users/:id" element={<VettedCommunityUserDetails />} />
                      <Route path="/events" element={<Events />} />
                      <Route path="/events/new" element={<ManageEvent />} />
                      <Route path="/events/:eventId/edit" element={<ManageEvent />} />
                      <Route path="/events/:eventId" element={<EventDetails />} />
                      <Route path="/aria-handsoff" element={<AriaHandsoff />} />
                      <Route path="/notifications" element={<PortalNotificationsPage />} />
                      <Route path="/partner-login" element={<Navigate replace to="/dashboard" />} />
                      <Route
                        path="/partner-dashboard"
                        element={<Navigate replace to="/dashboard" />}
                      />
                      <Route path="*" element={<Navigate replace to="/dashboard" />} />
                    </Routes>
                  </EventsScopeProvider>
                </Layout>
              )}
            </SocketProvider>
          </NotificationProvider>
        </Router>
      )}
    </ThemeModeProvider>
  );
};

export default App;
